import { Component, Inject, Input } from '@angular/core';
import { FileManagerService } from '../../services/file-manager.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActionType, ContentStatus, FileData, IFileActionProgress } from '../../file-manager/fileData';
import { FileManagerDeleteItemComponent } from '../file-manager-delete-item/file-manager-delete-item.component';
import { TreeNode } from '../../file-manager/htmlTreeNode';
import { FileSearchState } from '../../file-manager/FileManagerState';
import {
  WorkPanelBarItem,
  WorkPanelBarItemType,
} from 'xsmodules/ticket/components/work-panel/models/work-panel-bar-items';
import { XsI18nPipe } from '@nx-fe/pipes';
import { Validation } from 'app/utils/validation.helper';
import { DEVICE_OS_FAMILY, FeatureToggleService, hd } from '@nx-fe/services';
import { FileManagerRenameItemComponent } from 'xsmodules/remote-control/remote-control-view/file-manager-rename-item/file-manager-rename-item.component';
import { DeviceStateRegistryService } from 'app/modules/remote-control/services/device-state-registry.service';
import { RcContext } from 'app/modules/remote-control-ng/context/rcContext';

@Component({
  selector: 'app-file-manager-action-bar',
  templateUrl: './file-manager-action-bar.component.html',
  styleUrls: ['./file-manager-action-bar.component.scss'],
})
export class FileManagerActionBarComponent {
  @Input() selectedTreeNodes: TreeNode[];
  private compressionFileExtensions = ['zip', 'rar', '7z'];

  public buttonItems: WorkPanelBarItem[] = [
    {
      name: 'Download',
      icon: () => 'hd-icon-download',
      type: WorkPanelBarItemType.Button,
      tooltip: () => this.saI18nPipe.transform('fileManager_actionBar_btn_Download'),
      onClick: button => this.downloadItem(),
      isVisible: () =>
        this.featureToggleService.isEnabled('FileManagerActionBarRenameDownloadDelete') &&
        !this.isDownloadButtonDisableOrNotVisible(),
      isDisabled: () => false,
    } as WorkPanelBarItem,
    {
      name: 'Delete',
      icon: () => 'hd-icon-delete',
      type: WorkPanelBarItemType.Button,
      tooltip: () =>
        this.isSameFileOrParentFolder
          ? this.saI18nPipe.transform('fileManager_actionBar_btn_Disabled')
          : this.saI18nPipe.transform('xs_delete_label'),
      onClick: button => this.deleteItem(button),
      isVisible: () =>
        this.featureToggleService.isEnabled('FileManagerActionBarRenameDownloadDelete') &&
        this.hasDeviceWritePermission() &&
        !this.isDriveSelected(),
      isDisabled: () => this.isSameFileOrParentFolder,
    } as WorkPanelBarItem,
    {
      name: 'CopyTo',
      icon: () => 'hd-icon-copy',
      type: WorkPanelBarItemType.Button,
      tooltip: () => this.saI18nPipe.transform('fileManager_actionBar_btn_CopyTo'),
      onClick: button => {},
      isVisible: () =>
        this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove') &&
        this.hasDeviceWritePermission(),
      isDisabled: () => false,
    } as WorkPanelBarItem,
    {
      name: 'Rename',
      icon: () => 'hd-icon-rename',
      type: WorkPanelBarItemType.Button,
      tooltip: () =>
        this.isSameFileOrParentFolder
          ? this.saI18nPipe.transform('fileManager_actionBar_btn_Disabled')
          : this.saI18nPipe.transform('fileManager_actionBar_btn_Rename'),
      onClick: button => this.renameItem(button),
      isVisible: () =>
        this.featureToggleService.isEnabled('FileManagerActionBarRenameDownloadDelete') &&
        this.hasDeviceWritePermission() &&
        !this.isDriveSelected(),
      isDisabled: () => this.isSameFileOrParentFolder,
    } as WorkPanelBarItem,
    {
      name: 'AttachToTicket',
      icon: () => 'hd-icon-attachment',
      type: WorkPanelBarItemType.Button,
      tooltip: () => this.saI18nPipe.transform('fileManager_actionBar_btn_AttachToTicket'),
      onClick: button => {},
      isVisible: () => this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove'),
      isDisabled: () => false,
    } as WorkPanelBarItem,
    {
      name: 'Compress',
      icon: () => 'hd-icon-file-compress',
      type: WorkPanelBarItemType.Button,
      tooltip: () => this.saI18nPipe.transform('fileManager_actionBar_btn_Compress'),
      onClick: button => this.compressItem(),
      isVisible: () => this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove'),
      isDisabled: () => !this.canCompressItem(),
    } as WorkPanelBarItem,
    {
      name: 'Move',
      icon: () => 'hd-icon-movefile',
      type: WorkPanelBarItemType.Button,
      tooltip: () => this.saI18nPipe.transform('fileManager_actionBar_btn_Move'),
      onClick: button => {},
      isVisible: () => this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove'),
      isDisabled: () => false,
    } as WorkPanelBarItem,
  ];

  constructor(
    private featureToggleService: FeatureToggleService,
    private fileManagerService: FileManagerService,
    private saI18nPipe: XsI18nPipe,

    private deviceStateRegistry: DeviceStateRegistryService,
    private dialog: MatDialog
  ) {}

  public isAnyButtonVisible = (): boolean => this.buttonItems.some((button: WorkPanelBarItem) => button.isVisible());

  public getVisibleButtons = (): WorkPanelBarItem[] => this.buttonItems.filter((button: WorkPanelBarItem) => button.isVisible());

  public get isSameFileOrParentFolder() {
    let isSameFileOrParentPath = false;
    const currentSelectionPath = this.currentSelectedItem() ? this.currentSelectedItem().data.fullPath : null;

    const statusCenterData = this.fileManagerService.getStatusCenterData();
    if (statusCenterData) {
      const filesUnderDownloadProcess = Array.from(statusCenterData.values()).filter(
        x => x.contentStatus === ContentStatus.InProgress || x.contentStatus === ContentStatus.CompressInProgress
      );

      isSameFileOrParentPath = filesUnderDownloadProcess.some(fileData =>
        this.isSameFileOrParentFolderSelected(this.getFilePath(fileData), currentSelectionPath)
      );
    }

    return isSameFileOrParentPath;
  }

  public isFileSelected = (): boolean => this.selectedTreeNodes && this.selectedTreeNodes.length > 0 && this.selectedTreeNodes[0].type === 'file';

  public isFolderSelected = (): boolean => this.selectedTreeNodes && this.selectedTreeNodes.length > 0 && this.selectedTreeNodes[0].type === 'folder';

  private isDriveSelected = (): boolean => this.selectedTreeNodes && this.selectedTreeNodes.length > 0 && this.selectedTreeNodes[0].type === 'drive';

  private downloadItem = (): void => {
    if (this.isFileSelected()) {
      this.fileManagerService.downloadSelectedItem();
    } else if (this.isFolderSelected()) {
      this.fileManagerService.addSelectedItemCompressionToTheQueue();
    }
  };

  private compressItem = (): void => {
    this.fileManagerService.addSelectedItemCompressionToTheQueue();
  };

  // Available on Android only
  // Agent 13.3 and up
  public canCompressItem = (): boolean => this.hasDeviceWritePermission() && this.fileManagerService.isCompressItemSupported();

  private isSameFileOrParentFolderSelected = (parentPath: string, childPath: string): boolean => {
    if (!parentPath || !childPath) {
      return false;
    }

    let isParentFolder = true;
    const childPathFolders = childPath.split('/');

    /** Check if all the parent folders and file name present in a path of the file being downloaded */
    if (childPathFolders.some(e => !parentPath.includes(e))) {
      isParentFolder = false;
    } else {
      const parentPathFolders = this.isWindowsDevice() ? parentPath.split('\\') : parentPath.split('/');

      /* If all the parent folders and file name of current selection present in a path
               of the file being downloaded then check for the equality of the hierarchy */
      for (let index = 0; index < childPathFolders.length; index++) {
        if (index === childPathFolders.length - 1) {
          /* if user downloads a folder multiple times then name of the compressed file becomes fileName(1).zip/7z/rar and so on.
           * Hence have used below two regular expressions to match the file path */

          const extensions = this.compressionFileExtensions.join('|');
          const extensionRegex = new RegExp(`^${this.escapeRegExp(childPathFolders[index])}\.${extensions}`);
          const multipleCopyExtensionRegex = new RegExp(
            `^${this.escapeRegExp(childPathFolders[index])}\\([0-9]+\\).${extensions}`
          );

          if (
            childPathFolders[index] !== parentPathFolders[index] &&
            !extensionRegex.test(parentPathFolders[index]) &&
            !multipleCopyExtensionRegex.test(parentPathFolders[index])
          ) {
            isParentFolder = false;
          }
        } else {
          if (childPathFolders[index] !== parentPathFolders[index]) {
            isParentFolder = false;
          }
        }
      }
    }

    return isParentFolder;
  };

  private escapeRegExp(str) {
    return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
  }

  private currentSelectedItem = () => this.selectedTreeNodes && this.selectedTreeNodes.length === 1 ? this.selectedTreeNodes[0] : null;

  private renameItem = (button: WorkPanelBarItem): void => {
    if (button.isDisabled()) {
      return;
    }

    const selectedItem = this.fileManagerService.getCurrentItem() as FileData;

    const dialogParams = {
      placeHolder: Validation.sanitizedWindowsFileName(selectedItem.fileName),
      regexFilter: Validation.windowsFileNameRegex,
      unallowedChar: '\\ * ? " : | < > / ; @ , \[ \]',
    } as hd.PromptDialogParams;

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = 'custom-dialog-container';
    dialogConfig.closeOnNavigation = true;

    const modalInstance = this.dialog.open(FileManagerRenameItemComponent, dialogConfig);
    modalInstance.componentInstance.dialogParams = dialogParams;

    modalInstance.afterClosed().subscribe((result: string) => {
      if (result !== null) {
        console.log('Rename Dialog closed: ', result);
      }
    });
  };

  private deleteItem = (button: WorkPanelBarItem): void => {
    if (button.isDisabled()) {
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.panelClass = 'custom-dialog-container';
    dialogConfig.closeOnNavigation = true;

    const modalInstance = this.dialog.open(FileManagerDeleteItemComponent, dialogConfig);
    modalInstance.afterClosed().subscribe((result: string) => {
      if (result !== null) {
        console.log('Delete Dialog closed: ', result);
      }
    });
  };

  private hasDeviceWritePermission = (): boolean => {
    const devicePermissions = this.deviceStateRegistry.getPermissions(RcContext.instance.sotiAssistDeviceId);
    return (
      devicePermissions &&
      devicePermissions.permissions &&
      (devicePermissions.permissions & hd.RemoteControlPermissions.RemoteControlUpdateFiles) !== 0
    );
  };

  private isWindowsDevice = (): boolean => {
    const deviceState = this.deviceStateRegistry.getState(RcContext.instance.sotiAssistDeviceId);
    if (!deviceState) {
      return false;
    }
    const device = deviceState.device;
    if (device && device.family) {
      return (
        device.family.indexOf(DEVICE_OS_FAMILY.WindowsCE) >= 0 ||
        device.family.indexOf(DEVICE_OS_FAMILY.WindowsDesktop) >= 0 ||
        device.family.indexOf(DEVICE_OS_FAMILY.WindowsModernDesktop) >= 0 ||
        device.family.indexOf(DEVICE_OS_FAMILY.WindowsPhone) >= 0
      );
    } else {
      return false;
    }
  };

  private getFilePath = (fileData: IFileActionProgress): string => fileData.actionType === ActionType.Compress
      ? fileData.location
      : fileData.location.concat(fileData.file.name);

  private isDownloadButtonDisableOrNotVisible = () => (this.isFolderSelected() || this.isDriveSelected()) && !this.canCompressItem();

  private isSearchMode = (): boolean => {
    const state = this.fileManagerService.getState(RcContext.instance.sotiAssistDeviceId);
    return state.searchState && state.searchState !== FileSearchState.Ready;
  };
}
